import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import mammoth from 'mammoth';
import {
  BeakerIcon,
  MicIcon,
  SquareIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  SendIcon,
  XIcon,
} from '../icons';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import FreeCreditsLabel from '../../components/FreeCreditsLabel';
import {
  createDoc,
  PDF,
  fR,
  sR,
  T,
  drawCoverHeader,
  drawPageHeader,
  drawFooters,
  drawSectionHeading,
} from '../../utils/pdfBrand';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/* ─────────────────────────────────────────────────────────────────────────
 * Mock Interview — Electron-native pane.
 *
 * Three views in one component:
 *   1. SetupView   — job + JD + resume form, fires generate-questions.
 *   2. LiveView    — question N/15 with mic recording per Q + timer.
 *   3. ResultsView — overall score, per-question feedback, PDF report.
 *
 * Uses the same backend endpoints as the website's MockInterviewPage so
 * results, PDF parsing, and grading remain identical:
 *   POST /api/mock-interview/deduct-credit
 *   POST /api/mock-interview/generate-questions
 *   POST /api/mock-interview/evaluate
 * ─────────────────────────────────────────────────────────────────────── */

async function extractText(file) {
  const name = (file.name || '').toLowerCase();
  if (file.type === 'text/plain' || name.endsWith('.txt')) return await file.text();
  if (file.type === 'application/pdf' || name.endsWith('.pdf')) {
    const buf = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    let out = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const c = await page.getTextContent();
      out += c.items.map(it => it.str).join(' ') + '\n';
    }
    return out.trim();
  }
  if (name.endsWith('.docx') || name.endsWith('.doc') || file.type.includes('word')) {
    const buf = await file.arrayBuffer();
    const r = await mammoth.extractRawText({ arrayBuffer: buf });
    return (r.value || '').trim();
  }
  throw new Error('Unsupported file. Use PDF, DOCX, DOC, or TXT.');
}

function buildResumeTextFromProfile(profile) {
  if (!profile) return '';
  let text = '';
  if (profile.fullName) text += `${profile.fullName}\n`;
  if (profile.jobTitle) text += `${profile.jobTitle}\n`;
  if (profile.location) text += `Location: ${profile.location}\n`;
  if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
  text += '\n';
  if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
  if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
  if (Array.isArray(profile.workExperience) && profile.workExperience.length) {
    text += 'WORK EXPERIENCE\n';
    for (const exp of profile.workExperience) {
      if (exp.position || exp.company) {
        text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
        if (exp.description) text += `  ${exp.description}\n`;
      }
    }
    text += '\n';
  }
  if (Array.isArray(profile.education) && profile.education.length) {
    text += 'EDUCATION\n';
    for (const ed of profile.education) {
      text += `${ed.degree || ''}${ed.school || ed.institution ? ` — ${ed.school || ed.institution}` : ''}${ed.year ? ` (${ed.year})` : ''}\n`;
    }
  }
  return text.trim();
}

function getConfidenceMeta(score) {
  if (score == null) return { color: '#888', label: 'N/A', cls: 'na' };
  if (score >= 70) return { color: '#10b981', label: 'High Confidence', cls: 'high' };
  if (score >= 40) return { color: '#f59e0b', label: 'Moderate', cls: 'mid' };
  return { color: '#ef4444', label: 'Low', cls: 'low' };
}

function fmtTime(seconds) {
  const m = String(Math.max(0, Math.floor(seconds / 60))).padStart(2, '0');
  const s = String(Math.max(0, seconds % 60)).padStart(2, '0');
  return `${m}:${s}`;
}

// Target question count for a full mock interview. Used by the
// background top-up to know when to stop fetching.
const MOCK_TOTAL_QUESTIONS = 15;

export default function MockInterviewPane() {
  // 'setup' | 'live' | 'results'
  const [view, setView] = useState('setup');
  const [jobData, setJobData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});      // { [idx]: { audio: Blob, recordedAt } }
  const [evaluation, setEvaluation] = useState(null);
  const [duration, setDuration] = useState(0);     // seconds spent in test

  // Tell the shell to enter "session mode" (auto-collapse sidebar, lock
  // navigation affordances) while the live mock interview is running.
  // ElectronShell listens for these events to flip its sidebar state.
  // Re-emits on view change so refresh / navigation can't leave the
  // shell in a stale session-collapsed state.
  useEffect(() => {
    if (view === 'live') {
      window.dispatchEvent(new CustomEvent('terview:session-started', { detail: { source: 'mock-interview' } }));
    } else {
      window.dispatchEvent(new CustomEvent('terview:session-ended', { detail: { source: 'mock-interview' } }));
    }
    return () => {
      // Component unmounting from `live` view (e.g. tab switch with
      // a partially-active session) — restore sidebar so the user
      // isn't stranded.
      if (view === 'live') {
        window.dispatchEvent(new CustomEvent('terview:session-ended', { detail: { source: 'mock-interview' } }));
      }
    };
  }, [view]);

  if (view === 'setup') {
    return (
      <SetupView
        onStart={(data, qs) => {
          setJobData(data);
          setQuestions(qs);
          setAnswers({});
          setEvaluation(null);
          setView('live');
        }}
      />
    );
  }
  if (view === 'live') {
    return (
      <LiveView
        jobData={jobData}
        questions={questions}
        setQuestions={setQuestions}
        totalQuestions={MOCK_TOTAL_QUESTIONS}
        answers={answers}
        setAnswers={setAnswers}
        onSubmit={(evalData, secondsSpent) => {
          setEvaluation(evalData);
          setDuration(secondsSpent);
          setView('results');
        }}
        onAbort={() => {
          setView('setup');
          setJobData(null);
          setQuestions([]);
          setAnswers({});
        }}
      />
    );
  }
  return (
    <ResultsView
      evaluation={evaluation}
      questions={questions}
      jobData={jobData}
      duration={duration}
      onRetake={() => {
        setView('setup');
        setJobData(null);
        setQuestions([]);
        setAnswers({});
        setEvaluation(null);
      }}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* SetupView                                                               */
/* ─────────────────────────────────────────────────────────────────────── */

function SetupView({ onStart }) {
  const [form, setForm] = useState({
    jobRole: '',
    company: '',
    experienceLevel: 'Mid-level',
    jobDescription: '',
  });
  const [resumeChoice, setResumeChoice] = useState('profile');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedText, setUploadedText] = useState('');
  const [parseError, setParseError] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [profile, setProfile] = useState({ data: null, hasResume: false, loading: true });
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const fileRef = useRef(null);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (cancelled) return;
        const profileObj = d?.profile;
        const filename = profileObj?.resumeFilename || null;
        setProfile({ data: profileObj, hasResume: !!filename, loading: false });
        if (!filename) setResumeChoice('upload');
      } catch {
        if (!cancelled) setProfile({ data: null, hasResume: false, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setParseError('');
    setIsParsing(true);
    try {
      const text = await extractText(file);
      if (!text) throw new Error('No text extracted from file.');
      setUploadedText(text);
    } catch (err) {
      setParseError(err.message || 'Failed to parse file.');
      setUploadedText('');
    } finally {
      setIsParsing(false);
    }
  };

  const canStart = (() => {
    if (!form.jobRole.trim() || !form.jobDescription.trim()) return false;
    if (resumeChoice === 'upload' && !uploadedText) return false;
    if (resumeChoice === 'profile' && !profile.hasResume) return false;
    return true;
  })();

  const gate = useFeatureGate('mockInterview');

  const handleStart = async () => {
    if (!canStart) return;
    if (gate.locked) { gate.openPaywall(); return; }
    setStarting(true);
    setError('');
    try {
      // 1) deduct credit via the shared gate (refreshes the counter chip
      // and opens the paywall on 403 without a manual modal here).
      const { ok, reason } = await gate.deduct();
      if (!ok) {
        if (reason === 'no-credits') { setStarting(false); return; }
        throw new Error(reason || 'Failed to start');
      }

      const resumeText = resumeChoice === 'upload'
        ? uploadedText
        : buildResumeTextFromProfile(profile.data);

      const body = {
        jobRole: form.jobRole.trim(),
        company: form.company.trim(),
        experienceLevel: form.experienceLevel,
        jobDescription: form.jobDescription.trim(),
        resumeText,
      };

      // 2) Generate ONLY the first question synchronously so we can
      // switch into the live view in ~1.5s instead of ~10s. The live
      // view fires a background top-up to fetch the remaining 14
      // questions while the candidate is reading / answering the first
      // one — they're typically on question 1 for 1-2 min, plenty of
      // time for the rest to arrive.
      const qr = await fetch(`${API_BASE_URL}/api/mock-interview/generate-questions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...body, count: 1 }),
      });
      if (!qr.ok) throw new Error('Failed to generate the first question.');
      const qd = await qr.json();
      const qs = Array.isArray(qd?.questions) ? qd.questions : [];
      if (!qs.length) throw new Error('No question returned by the server.');
      onStart(body, qs);
    } catch (err) {
      setError(err.message || 'Could not start the mock interview.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="pane">
      <div className="pane-eyebrow">
        <BeakerIcon size={11} /> Practice Test
      </div>
      <h1 className="pane-title">Mock Interview</h1>

      <div className="pane-card">
        <div className="pane-row">
          <div className="pane-field">
            <label className="pane-label">Job role *</label>
            <input className="pane-input" placeholder="e.g. Senior Software Engineer" value={form.jobRole} onChange={update('jobRole')} />
          </div>
          <div className="pane-field">
            <label className="pane-label">Company</label>
            <input className="pane-input" placeholder="e.g. Twilio" value={form.company} onChange={update('company')} />
          </div>
        </div>

        <div className="pane-field">
          <label className="pane-label">Experience level</label>
          <select className="pane-select" value={form.experienceLevel} onChange={update('experienceLevel')}>
            <option value="Entry-level">Entry-level (0-2 yrs)</option>
            <option value="Mid-level">Mid-level (3-5 yrs)</option>
            <option value="Senior">Senior (6-10 yrs)</option>
            <option value="Staff/Principal">Staff / Principal (10+ yrs)</option>
          </select>
        </div>

        <div className="pane-field">
          <label className="pane-label">Job description *</label>
          <textarea
            className="pane-textarea"
            placeholder="Paste the JD. The more detail, the more targeted the question generation."
            value={form.jobDescription}
            onChange={update('jobDescription')}
          />
        </div>

        <div className="pane-field">
          <label className="pane-label">Resume *</label>
          {/* Side-by-side cards in a 2-col grid to save vertical space —
              matches the AI Interview Assistant pane styling. */}
          <div className="es-resume-choices">
            <ResumeChoiceCard
              checked={resumeChoice === 'profile'}
              disabled={!profile.hasResume}
              onClick={() => profile.hasResume && setResumeChoice('profile')}
              label="Use my profile resume"
              status={
                profile.loading ? <span style={{ color: 'var(--es-text-subtle)' }}>(checking…)</span>
                : profile.hasResume ? (
                  <span className="es-choice-status ok"><CheckCircleIcon size={12} /> {profile.data?.resumeFilename}</span>
                ) : (
                  <span className="es-choice-status warn"><AlertTriangleIcon size={12} /> no resume on profile</span>
                )
              }
            />
            <ResumeChoiceCard
              checked={resumeChoice === 'upload'}
              onClick={() => setResumeChoice('upload')}
              label="Upload a different resume"
              status={uploadedFile && (
                <span className="es-choice-status ok">
                  <CheckCircleIcon size={12} /> {uploadedFile.name}
                  {isParsing && <span style={{ color: 'var(--es-text-subtle)', marginLeft: 6 }}>parsing…</span>}
                </span>
              )}
              action={resumeChoice === 'upload' && (
                <button type="button" className="es-choice-action" onClick={() => fileRef.current?.click()}>
                  {uploadedFile ? 'Replace file' : 'Choose file'}
                </button>
              )}
            />
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" onChange={onFile} style={{ display: 'none' }} />
          {parseError && <div className="pane-error">{parseError}</div>}
        </div>

        {error && <div className="pane-error">{error}</div>}
      </div>

      <button
        type="button"
        className="pane-cta"
        onClick={handleStart}
        disabled={!canStart || starting || gate.locked}
        title={gate.locked ? 'Out of free credits — upgrade to keep using' : ''}
      >
        {starting ? 'Generating questions…' : <>Start Mock Test → <FreeCreditsLabel feature="mockInterview" /></>}
      </button>
    </div>
  );
}

function ResumeChoiceCard({ checked, disabled, onClick, label, status, action }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={`es-choice ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <span className="es-choice-radio" aria-hidden="true">
        <span className="es-choice-radio-dot" />
      </span>
      <div className="es-choice-body">
        <div className="es-choice-label">{label}</div>
        {status && <div className="es-choice-meta">{status}</div>}
        {action && <div className="es-choice-actions">{action}</div>}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* LiveView                                                                */
/* ─────────────────────────────────────────────────────────────────────── */

function LiveView({ jobData, questions, setQuestions, totalQuestions = 15, answers, setAnswers, onSubmit, onAbort }) {
  const [idx, setIdx] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(45 * 60);
  const [isRecording, setIsRecording] = useState(false);
  const [recError, setRecError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  // Status of the background top-up that fills out questions 2..N
  // while the candidate answers question 1. Surfaced as a small pill
  // in the header so they know more are coming.
  const [topupStatus, setTopupStatus] = useState('idle'); // 'idle' | 'loading' | 'done' | 'error'
  const topupFiredRef = useRef(false);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(45 * 60);

  useEffect(() => { startedAtRef.current = secondsLeft; /* set once on mount */ }, []); // eslint-disable-line

  // Background question top-up — runs once on mount. We arrived with
  // just Q1 (so the session opened fast); fetch the remaining
  // `totalQuestions - 1` questions while the candidate is reading Q1.
  // Server is told which questions we already have so it doesn't
  // duplicate them.
  useEffect(() => {
    if (topupFiredRef.current) return;
    topupFiredRef.current = true;
    if (!setQuestions || !jobData || questions.length >= totalQuestions) return;
    const needed = totalQuestions - questions.length;
    if (needed <= 0) return;
    setTopupStatus('loading');
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/mock-interview/generate-questions`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...jobData,
            count: needed,
            existingQuestions: questions.map(q => q.question).filter(Boolean),
          }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        const extra = Array.isArray(data?.questions) ? data.questions : [];
        if (extra.length) {
          setQuestions(prev => [...prev, ...extra].slice(0, totalQuestions));
        }
        setTopupStatus('done');
      } catch (e) {
        console.warn('[MockInterview] background top-up failed:', e?.message);
        setTopupStatus('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown — auto-submits when it hits 0.
  useEffect(() => {
    const t = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) {
          clearInterval(t);
          // Defer submit to next tick to avoid setState during cleanup.
          setTimeout(() => doSubmit(true), 0);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh the audio preview URL whenever the active question's answer changes.
  useEffect(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const blob = answers[idx]?.audio;
    setPreviewUrl(blob ? URL.createObjectURL(blob) : '');
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, answers[idx]?.audio]);

  const startRec = async () => {
    setRecError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      chunksRef.current = [];
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        setAnswers(prev => ({
          ...prev,
          [idx]: { audio: blob, recordedAt: new Date().toISOString() },
        }));
      };
      rec.start();
      recorderRef.current = rec;
      setIsRecording(true);
    } catch (err) {
      setRecError(
        err?.name === 'NotAllowedError'
          ? 'Microphone access denied. Allow it in System Settings → Privacy → Microphone, then try again.'
          : `Could not access microphone: ${err.message || err}`
      );
    }
  };

  const stopRec = () => {
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    recorderRef.current = null;
    setIsRecording(false);
  };

  const deleteAnswer = () => {
    setAnswers(prev => {
      const n = { ...prev };
      delete n[idx];
      return n;
    });
  };

  const doSubmit = async (auto = false) => {
    if (submitting) return;
    if (!auto) {
      const unanswered = questions.length - Object.keys(answers).length;
      if (unanswered > 0) {
        const ok = window.confirm(`You have ${unanswered} unanswered question(s). Submit anyway?`);
        if (!ok) return;
      }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('jobRole', jobData.jobRole);
      fd.append('company', jobData.company || '');
      fd.append('experienceLevel', jobData.experienceLevel);
      fd.append('jobDescription', jobData.jobDescription);
      fd.append('resumeText', jobData.resumeText);
      fd.append('questions', JSON.stringify(questions));
      const secondsSpent = startedAtRef.current - secondsLeft;
      fd.append('duration', String(secondsSpent));
      Object.entries(answers).forEach(([qi, a]) => {
        fd.append(`audio_${qi}`, a.audio, `answer_${qi}.webm`);
      });
      const r = await fetch(`${API_BASE_URL}/api/mock-interview/evaluate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      if (!r.ok) throw new Error('Failed to evaluate the test.');
      const evalData = await r.json();
      onSubmit(evalData, secondsSpent);
    } catch (err) {
      alert(err.message || 'Failed to submit. Please try again.');
      setSubmitting(false);
    }
  };

  const timerTone = secondsLeft <= 60 ? 'danger' : secondsLeft <= 300 ? 'warn' : '';
  const answeredCount = Object.keys(answers).length;
  const q = questions[idx] || {};
  const hasAnswer = !!answers[idx]?.audio;

  return (
    <div className="mip-root">
      <div className="ilv-topbar">
        <div className="ilv-job">
          <div className="ilv-job-role">{jobData.jobRole} — Mock Interview</div>
          <div className="ilv-job-company">
            Question {idx + 1} of {totalQuestions} · {answeredCount} answered
            {topupStatus === 'loading' && (
              <span className="mip-topup-pill loading"> · loading more…</span>
            )}
            {topupStatus === 'error' && (
              <span className="mip-topup-pill error" title="Background top-up failed; you still have the first question."> · top-up failed</span>
            )}
          </div>
        </div>
        <div className={`ilv-timer ${timerTone}`}>
          <span className="ilv-timer-dot" />
          <span className="ilv-timer-text">{fmtTime(secondsLeft)}</span>
        </div>
        <div className="ilv-actions">
          <button
            type="button"
            className="ilv-end-btn"
            onClick={() => {
              if (window.confirm('End the test? Your progress will be lost.')) onAbort();
            }}
          >
            End test
          </button>
        </div>
      </div>

      {/* Question chips — quick jump + at-a-glance answered/unanswered.
          Slots beyond questions.length are pending until the top-up
          fetch lands; we render them disabled so the candidate sees the
          full 15 from the start. */}
      <div className="mip-chips">
        {Array.from({ length: totalQuestions }, (_, i) => {
          const loaded = i < questions.length;
          return (
            <button
              key={i}
              type="button"
              className={`mip-chip ${i === idx ? 'active' : ''} ${answers[i] ? 'done' : ''} ${loaded ? '' : 'pending'}`}
              onClick={() => loaded && !isRecording && setIdx(i)}
              disabled={!loaded}
              title={
                !loaded ? `Question ${i + 1} — loading…`
                : answers[i] ? `Question ${i + 1} — answered`
                : `Question ${i + 1}`
              }
            >
              {answers[i] ? <CheckCircleIcon size={11} /> : <span>{i + 1}</span>}
            </button>
          );
        })}
      </div>

      {/* Question card */}
      <div className="mip-question-card">
        <div className="mip-q-num">Q{idx + 1}</div>
        <div className="mip-q-text">{q.question || '—'}</div>
        {q.category && (
          <div className="mip-q-category">{q.category}</div>
        )}
      </div>

      {/* Answer panel — record / replay / delete */}
      <div className="mip-answer-panel">
        {!hasAnswer && !isRecording && (
          <div className="mip-answer-hint">
            <MicIcon size={18} />
            <span>Click record and answer the question out loud. You can re-record or delete before moving on.</span>
          </div>
        )}

        {isRecording && (
          <div className="mip-recording">
            <div className="mip-rec-pulse" />
            <span>Recording…</span>
          </div>
        )}

        {hasAnswer && !isRecording && (
          <div className="mip-playback">
            <audio src={previewUrl} controls className="mip-audio" />
            <span className="mip-playback-meta">
              <CheckCircleIcon size={13} /> Answer saved
            </span>
          </div>
        )}

        <div className="mip-answer-actions">
          {!isRecording ? (
            <button
              type="button"
              className={`mip-rec-btn ${hasAnswer ? 'redo' : ''}`}
              onClick={startRec}
              disabled={submitting}
            >
              <MicIcon size={15} /> {hasAnswer ? 'Re-record' : 'Record answer'}
            </button>
          ) : (
            <button type="button" className="mip-rec-btn stop" onClick={stopRec}>
              <SquareIcon size={13} /> Stop
            </button>
          )}
          {hasAnswer && !isRecording && (
            <button type="button" className="mip-delete-btn" onClick={deleteAnswer}>
              <XIcon size={13} /> Delete
            </button>
          )}
        </div>

        {recError && <div className="pane-error" style={{ marginTop: 12 }}>{recError}</div>}
      </div>

      {/* Footer nav: prev / next / submit */}
      <div className="mip-nav">
        <button
          type="button"
          className="mip-nav-btn"
          onClick={() => setIdx(i => Math.max(0, i - 1))}
          disabled={idx === 0 || isRecording || submitting}
        >
          ← Previous
        </button>
        <div className="mip-nav-spacer" />
        {idx < totalQuestions - 1 ? (() => {
          // Next button — disabled if the next question hasn't been
          // generated yet by the background top-up. The user can keep
          // answering the current one while we wait.
          const nextLoaded = idx + 1 < questions.length;
          return (
            <button
              type="button"
              className="mip-nav-btn primary"
              onClick={() => nextLoaded && setIdx(i => Math.min(totalQuestions - 1, i + 1))}
              disabled={isRecording || submitting || !nextLoaded}
              title={!nextLoaded ? 'Next question is still loading…' : ''}
            >
              {nextLoaded ? 'Next →' : 'Loading next…'}
            </button>
          );
        })() : (
          <button
            type="button"
            className="mip-nav-btn primary"
            onClick={() => doSubmit(false)}
            disabled={isRecording || submitting || questions.length < totalQuestions}
            title={questions.length < totalQuestions ? 'Waiting for the rest of the questions…' : ''}
          >
            {submitting ? 'Evaluating…' : <>Submit <SendIcon size={13} /></>}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* ResultsView                                                             */
/* ─────────────────────────────────────────────────────────────────────── */

function ResultsView({ evaluation, questions, jobData, duration, onRetake }) {
  if (!evaluation) {
    return (
      <div className="pane">
        <h1 className="pane-title">No results yet</h1>
        <button className="pane-cta" onClick={onRetake}>Start a new mock test →</button>
      </div>
    );
  }

  const overall = evaluation.overallScore || 0;
  const scores = evaluation.questionScores || [];
  const avgConf = scores.length
    ? Math.round(scores.reduce((s, r) => s + (r.confidenceScore || 0), 0) / scores.length)
    : 0;
  const answered = scores.filter(r => (r.score > 0) || (r.contentScore > 0)).length;
  const confMeta = getConfidenceMeta(avgConf);

  const downloadReport = () => {
    try {
      const doc = createDoc();
      const { W, H, M, CW } = PDF;
      let y = 0;

      const sRgb = (s) => s >= 70 ? [22, 163, 74] : s >= 50 ? [202, 138, 4] : [220, 38, 38];
      const cRgb = (s) => (s || 0) >= 70 ? [22, 163, 74] : (s || 0) >= 40 ? [202, 138, 4] : [220, 38, 38];
      const newPage = () => { doc.addPage(); y = drawPageHeader(doc, 'Mock Interview Report'); };

      y = drawCoverHeader(doc, 'Mock Interview Report', `${jobData.jobRole}${jobData.company ? ' · ' + jobData.company : ''}`);

      // Summary block
      drawSectionHeading(doc, 'Summary', y);
      y += 10;
      doc.setFont(...fR).setFontSize(11).setTextColor(...T);
      doc.text(`Overall Score: ${overall}%`, M, y); y += 6;
      doc.text(`Avg Confidence: ${avgConf}%`, M, y); y += 6;
      doc.text(`Answered: ${answered} / ${scores.length}`, M, y); y += 6;
      doc.text(`Duration: ${fmtTime(duration)}`, M, y); y += 10;

      if (evaluation.generalFeedback) {
        drawSectionHeading(doc, 'General Feedback', y);
        y += 10;
        const lines = doc.splitTextToSize(evaluation.generalFeedback, CW);
        doc.setFont(...fR).setFontSize(10).setTextColor(...T);
        lines.forEach((line) => {
          if (y > H - 25) newPage();
          doc.text(line, M, y);
          y += 5;
        });
        y += 6;
      }

      scores.forEach((r, i) => {
        if (y > H - 60) newPage();
        drawSectionHeading(doc, `Q${i + 1}`, y);
        y += 9;

        doc.setFont(...sR).setFontSize(11).setTextColor(...T);
        const qText = questions[i]?.question || '—';
        const qLines = doc.splitTextToSize(qText, CW);
        qLines.forEach((line) => { if (y > H - 25) newPage(); doc.text(line, M, y); y += 5; });
        y += 2;

        doc.setFont(...fR).setFontSize(10);
        const [sR_r, sG, sB] = sRgb(r.score || 0);
        doc.setTextColor(sR_r, sG, sB);
        doc.text(`Score: ${r.score || 0}/100`, M, y); y += 6;
        const [cR_r, cG, cB] = cRgb(r.confidenceScore);
        doc.setTextColor(cR_r, cG, cB);
        doc.text(`Confidence: ${r.confidenceScore ?? '—'}%`, M, y); y += 8;

        doc.setTextColor(...T);
        if (r.advice) {
          const aLines = doc.splitTextToSize(`Advice: ${r.advice}`, CW);
          aLines.forEach((line) => { if (y > H - 25) newPage(); doc.text(line, M, y); y += 5; });
          y += 4;
        }

        if (questions[i]?.expectedAnswer) {
          const eLines = doc.splitTextToSize(`Model answer: ${questions[i].expectedAnswer}`, CW);
          eLines.forEach((line) => { if (y > H - 25) newPage(); doc.text(line, M, y); y += 5; });
          y += 6;
        }
      });

      drawFooters(doc);
      doc.save(`mock-interview-${(jobData.jobRole || 'report').replace(/\s+/g, '-').toLowerCase()}.pdf`);
    } catch (err) {
      console.error('PDF export failed', err);
      alert('Could not generate the PDF report.');
    }
  };

  return (
    <div className="mip-root">
      <div className="mip-summary">
        <div className="mip-summary-head">
          <div>
            <div className="pane-eyebrow"><BeakerIcon size={11} /> Test Complete</div>
            <h1 className="pane-title" style={{ marginTop: 8 }}>Mock Interview Results</h1>
            <p className="pane-subtitle" style={{ marginBottom: 0 }}>
              {jobData.jobRole}{jobData.company && ` · ${jobData.company}`} · {fmtTime(duration)} elapsed
            </p>
          </div>
          <div className="mip-summary-actions">
            <button type="button" className="pane-cta-secondary" onClick={onRetake}>Take another</button>
            <button type="button" className="pane-cta" onClick={downloadReport}>
              <SendIcon size={13} /> Download PDF
            </button>
          </div>
        </div>

        <div className="mip-summary-grid">
          <ScoreCircle value={overall} label="Overall Score" tone={overall >= 70 ? 'good' : overall >= 50 ? 'mid' : 'low'} />
          <Stat label="Avg Confidence" value={`${avgConf}%`} tone={confMeta.cls} sub={confMeta.label} />
          <Stat label="Answered" value={`${answered}/${scores.length}`} sub={`${Math.round((answered / Math.max(1, scores.length)) * 100)}% complete`} />
        </div>

        <div className="mip-overall-bar">
          <div className="mip-overall-bar-track">
            <div className="mip-overall-bar-fill" style={{ width: `${overall}%` }} />
          </div>
          <span className="mip-overall-bar-label">Readiness</span>
        </div>
      </div>

      {evaluation.generalFeedback && (
        <div className="mip-general-feedback">
          <div className="mip-section-label">General feedback</div>
          <p>{evaluation.generalFeedback}</p>
        </div>
      )}

      <div className="mip-results-list">
        {scores.map((r, i) => (
          <QuestionResult key={i} index={i} result={r} question={questions[i]} />
        ))}
      </div>
    </div>
  );
}

function ScoreCircle({ value, label, tone = 'mid' }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  // Stroke-dash trick on a 36-circumference SVG circle (r ≈ 5.7).
  const circ = 2 * Math.PI * 38;
  const dash = (pct / 100) * circ;
  const colorVar = tone === 'good' ? 'var(--es-success)' : tone === 'low' ? 'var(--es-danger)' : 'var(--es-warning)';
  return (
    <div className="mip-score-circle">
      <svg viewBox="0 0 100 100" width="92" height="92" aria-hidden="true">
        <circle cx="50" cy="50" r="38" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="9" />
        <circle
          cx="50" cy="50" r="38" fill="none"
          stroke={colorVar} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          transform="rotate(-90 50 50)"
        />
      </svg>
      <div className="mip-score-value">{pct}<span>%</span></div>
      <div className="mip-score-label">{label}</div>
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`mip-stat ${tone || ''}`}>
      <div className="mip-stat-label">{label}</div>
      <div className="mip-stat-value">{value}</div>
      {sub && <div className="mip-stat-sub">{sub}</div>}
    </div>
  );
}

function QuestionResult({ index, result, question }) {
  const cm = getConfidenceMeta(result.confidenceScore);
  const fillerCount = result.fillerWords || 0;
  const pace = result.pace || 0;
  const paceLabel = pace > 160 ? 'Fast' : pace > 110 ? 'Good' : pace > 0 ? 'Slow' : '—';
  const paceCls = pace > 160 ? 'fast' : pace > 110 ? 'good' : pace > 0 ? 'slow' : '';
  const score = result.score || 0;
  const scoreClass = score >= 70 ? 'good' : score >= 50 ? 'mid' : 'low';
  return (
    <div className="mip-qr">
      <div className="mip-qr-head">
        <div className="mip-qr-title">
          <span className="mip-qr-num">Q{index + 1}</span>
          <span>{question?.question || '—'}</span>
        </div>
        <span className={`mip-qr-score ${scoreClass}`}>{score}/100</span>
      </div>

      {question?.expectedAnswer && (
        <div className="mip-qr-section">
          <div className="mip-section-label">Model answer</div>
          <p>{question.expectedAnswer}</p>
        </div>
      )}

      <div className="mip-metrics">
        <Metric label="Content"      value={`${result.contentScore || 0}/50`}      barPct={((result.contentScore || 0) / 50) * 100} />
        <Metric label="Authenticity" value={`${result.authenticityScore || 0}/50`} barPct={((result.authenticityScore || 0) / 50) * 100} />
        <Metric
          label="Pace"
          value={pace > 0 ? `${pace} wpm` : '—'}
          tag={pace > 0 ? paceLabel : null}
          tone={paceCls}
        />
        <Metric
          label="Fillers"
          value={String(fillerCount)}
          tag={fillerCount > 5 ? 'Too many' : fillerCount > 2 ? 'A few' : 'Clean'}
          tone={fillerCount > 5 ? 'warn' : fillerCount > 2 ? 'mid' : 'ok'}
        />
      </div>

      <div className="mip-confidence">
        <div className="mip-confidence-row">
          <span className="mip-section-label">Confidence</span>
          <span className={`mip-conf-pct ${cm.cls}`}>
            {result.confidenceScore ?? '—'}%
            <span className="mip-conf-tag">{cm.label}</span>
            <span className={`mip-delivery ${result.isReading ? 'reading' : 'authentic'}`}>
              {result.isReading ? 'Reading detected' : 'Authentic delivery'}
            </span>
          </span>
        </div>
        <div className="mip-conf-track">
          <div
            className={`mip-conf-fill ${cm.cls}`}
            style={{ width: `${result.confidenceScore ?? 0}%` }}
          />
        </div>
      </div>

      {result.advice && (
        <div className="mip-qr-section">
          <div className="mip-section-label">Advice</div>
          <p>{result.advice}</p>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, barPct, tag, tone }) {
  return (
    <div className="mip-metric">
      <div className="mip-metric-label">{label}</div>
      <div className={`mip-metric-value ${tone || ''}`}>{value}</div>
      {barPct != null && (
        <div className="mip-metric-bar">
          <div className="mip-metric-bar-fill" style={{ width: `${Math.min(100, Math.max(0, barPct))}%` }} />
        </div>
      )}
      {tag && <div className={`mip-metric-tag ${tone || ''}`}>{tag}</div>}
    </div>
  );
}
